import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { WordmarkTagline } from "@/components/Logo";
import { api } from "@/lib/api";
import { formatCAD } from "@/lib/utils-cart";
import { ArrowRight, Package, Store, Megaphone, Sparkles, Wallet, Truck, Briefcase, BarChart3, ShieldCheck, CheckCircle2, Star } from "lucide-react";

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/products?limit=8").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
    api.get("/system/health").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute top-20 right-0 w-1/3 h-2/3 bg-gradient-to-bl from-secondary/10 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-secondary mb-6" data-testid="hero-eyebrow">
              The Commerce Operating System · Canada
            </div>
            <h1 className="font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-primary">
              Build a real <br />
              commerce business —<br />
              <span className="text-secondary">not just another store.</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Vendora unifies sourcing, selling, shipping and growth into one platform. Tap into vetted Canadian suppliers, launch a white-label store, recruit affiliates, and grow with built-in CRM, AI marketing, and analytics.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" className="v-btn-primary text-base px-7 py-3" data-testid="hero-cta-start">
                Start free <ArrowRight size={16} />
              </Link>
              <Link to="/marketplace" className="v-btn-outline text-base px-7 py-3" data-testid="hero-cta-explore">
                Explore marketplace
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-accent-green" /> No setup fee</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-accent-green" /> Centralized payments</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-accent-green" /> CAD-native</span>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <div className="v-card aspect-square flex flex-col justify-between">
              <Package className="text-secondary" size={28} />
              <div>
                <div className="font-display font-black text-3xl text-primary">{stats?.products ?? "18+"}</div>
                <div className="text-xs text-muted-foreground mt-1">curated products</div>
              </div>
            </div>
            <div className="v-card aspect-square flex flex-col justify-between bg-primary text-primary-foreground border-primary">
              <Wallet size={28} />
              <div>
                <div className="font-display font-black text-3xl">5%</div>
                <div className="text-xs text-white/70 mt-1">flat platform fee</div>
              </div>
            </div>
            <div className="v-card aspect-square flex flex-col justify-between bg-secondary text-secondary-foreground border-secondary">
              <Sparkles size={28} />
              <div>
                <div className="font-display font-black text-3xl">AI</div>
                <div className="text-xs text-white/80 mt-1">marketing built-in</div>
              </div>
            </div>
            <div className="v-card aspect-square flex flex-col justify-between">
              <Truck className="text-accent-blue" size={28} />
              <div>
                <div className="font-display font-black text-3xl text-primary">7d</div>
                <div className="text-xs text-muted-foreground mt-1">payout hold</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-t border-border bg-primary text-primary-foreground">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-center">
            <WordmarkTagline inverted />
          </div>
        </div>
      </section>

      {/* The Four Verbs */}
      <section className="v-section-pad border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-24">
              <div className="label-eyebrow text-secondary mb-4">Four verbs. One platform.</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">
                Everything you need to run<br />a commerce business in Canada.
              </h2>
              <p className="mt-6 text-muted-foreground">
                Stop stitching together five tools. Vendora is one stack — from the wholesale catalog to the customer's inbox.
              </p>
            </div>

            <div className="lg:col-span-8 grid md:grid-cols-2 gap-3">
              {[
                { icon: Package, color: "text-primary", verb: "SOURCE", title: "A real supplier network", body: "Browse vetted Canadian suppliers. Add inventory to your store in clicks." },
                { icon: Store, color: "text-secondary", verb: "SELL", title: "White-label storefronts", body: "Launch a branded store at vendora.ca/store/your-name with custom colors, banners, and pages." },
                { icon: Truck, color: "text-accent-blue", verb: "SHIP", title: "Centralized fulfillment", body: "One inbox for orders, tracking, returns. Partner with our fulfillment network or use your own." },
                { icon: BarChart3, color: "text-accent-green", verb: "GROW", title: "Marketing, CRM, analytics", body: "AI-generated product copy, email & SMS campaigns, customer segments, and unified analytics." },
              ].map((f) => (
                <div key={f.verb} className="v-card flex flex-col">
                  <div className={`label-eyebrow ${f.color}`}>{f.verb}</div>
                  <f.icon size={32} strokeWidth={1.5} className={`${f.color} mt-6 mb-8`} />
                  <h3 className="font-display text-xl font-semibold text-primary">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace teaser */}
      <section className="v-section-pad bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-eyebrow text-secondary mb-3">SOURCE.</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">From the Vendora Marketplace</h2>
            </div>
            <Link to="/marketplace" className="v-btn-outline" data-testid="marketplace-see-all">See all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {products.slice(0, 8).map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group v-card p-3 flex flex-col" data-testid={`marketplace-product-${p.id}`}>
                <div className="aspect-square overflow-hidden bg-muted">
                  <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="pt-3 flex-1 flex flex-col">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{p.category}</div>
                  <h3 className="text-sm font-semibold text-primary mt-1 leading-snug line-clamp-2">{p.title}</h3>
                  <div className="mt-auto pt-2 font-display font-bold text-lg text-foreground">{formatCAD(p.price)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Store Builder showcase */}
      <section className="v-section-pad border-b border-border">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow text-secondary mb-3">SELL.</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">Your store. Your brand. Live in minutes.</h2>
            <p className="mt-6 text-muted-foreground max-w-md">
              Pick a slug, drop your logo, choose your colors. Vendora hosts everything: catalog, checkout, payments, fulfillment, and even your customer support inbox.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {["Custom subdomain or vendora.ca/store/your-slug", "Branded checkout with Square + PayPal", "Mobile-first responsive themes", "Built-in SEO, reviews, and analytics"].map((x) => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-accent-green mt-0.5" /> {x}</li>
              ))}
            </ul>
            <Link to="/register" className="v-btn-primary mt-10" data-testid="storebuilder-cta">Launch a store <ArrowRight size={16} /></Link>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -left-4 right-8 bottom-8 bg-secondary/10 rounded-sm" />
            <div className="relative v-card p-0 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1753351052617-62818ffc9173?w=900&q=80" alt="merchant" className="w-full h-[420px] object-cover" />
              <div className="p-6">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-accent-green" />
                  <span className="font-mono text-xs tracking-wider">vendora.ca/store/coastline-studio</span>
                </div>
                <h3 className="font-display text-xl font-bold text-primary mt-2">Coastline Studio</h3>
                <p className="text-sm text-muted-foreground mt-1">Coastal-inspired homeware, candles & gifts.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate network */}
      <section className="v-section-pad bg-primary text-primary-foreground border-b border-primary/40 relative noise-overlay overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-secondary mb-4">GROW.</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-none">The affiliate & creator network you don't have to build.</h2>
            <p className="mt-6 text-white/70 max-w-xl">
              Every Vendora seller gets access to thousands of affiliates and creators who can promote your products. Generate referral links, track conversions, and pay commissions automatically.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {[["10%", "default commission"], ["7d", "approval window"], ["∞", "links per affiliate"]].map(([n, l]) => (
                <div key={n} className="border border-white/15 p-4 rounded-sm">
                  <div className="font-display font-black text-3xl">{n}</div>
                  <div className="text-[11px] text-white/60 mt-1 uppercase tracking-wider">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/15 rounded-sm p-6 bg-white/5 backdrop-blur">
              <div className="label-eyebrow text-secondary mb-4">Live affiliate dashboard</div>
              <div className="space-y-3 font-mono text-xs">
                <Row left="VND-2A8C" right="$129.00 · paid · square" />
                <Row left="VND-D7F1" right="$249.99 · paid · paypal" />
                <Row left="VND-92BB" right="$48.00 · pending · square" />
                <Row left="VND-1FA4" right="$189.00 · paid · paypal" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business services */}
      <section className="v-section-pad bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="label-eyebrow text-secondary mb-3">Beyond commerce</div>
              <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">A marketplace for the rest of your business.</h2>
              <p className="mt-4 text-muted-foreground max-w-2xl">Accounting, payroll, legal, insurance, IT, cybersecurity — vetted Canadian providers, transparent pricing, paid through Vendora.</p>
            </div>
            <Link to="/services" className="v-btn-outline">Browse services <ArrowRight size={14} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {services.map((s) => (
              <div key={s.id} className="v-card flex flex-col">
                <div className="label-eyebrow text-secondary">{s.category.replace("_", " ")}</div>
                <h3 className="mt-4 font-display text-lg font-semibold text-primary">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground flex-1">{s.description}</p>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                  <span className="font-mono">{formatCAD(s.price_from)} / {s.pricing_unit}</span>
                  <span className="flex items-center gap-1 text-amber-600"><Star size={14} fill="currentColor" /> {s.rating}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section id="pricing" className="v-section-pad border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="label-eyebrow text-secondary mb-3">Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">No subscriptions. One flat fee.</h2>
            <p className="mt-4 text-muted-foreground">5% per transaction. That's it. We only make money when you do.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-3 max-w-5xl mx-auto">
            {[
              { name: "Starter", price: "Free", subtitle: "Up to 50 orders / mo", features: ["1 storefront", "Square + PayPal checkout", "AI marketing (50 credits)", "Email support"] },
              { name: "Scale", price: "5% per sale", featured: true, subtitle: "Unlimited orders", features: ["Unlimited storefronts", "Unlimited AI marketing", "Affiliate network access", "Priority support"] },
              { name: "Enterprise", price: "Custom", subtitle: "Volume-based pricing", features: ["Multi-warehouse", "White-glove onboarding", "API access", "Dedicated CSM"] },
            ].map((p) => (
              <div key={p.name} className={`v-card flex flex-col ${p.featured ? "border-secondary border-2" : ""}`}>
                {p.featured && <div className="label-eyebrow text-secondary mb-2">Most popular</div>}
                <h3 className="font-display text-xl font-bold text-primary">{p.name}</h3>
                <div className="mt-2 font-display font-black text-4xl text-foreground">{p.price}</div>
                <p className="mt-1 text-xs text-muted-foreground">{p.subtitle}</p>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {p.features.map((f) => <li key={f} className="flex gap-2"><CheckCircle2 size={16} className="text-accent-green flex-shrink-0 mt-0.5" /> {f}</li>)}
                </ul>
                <Link to="/register" className={`mt-6 ${p.featured ? "v-btn-primary" : "v-btn-outline"}`}>Get started</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="v-section-pad bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="label-eyebrow text-secondary mb-3 text-center">Trusted by Canadian operators</div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary text-center leading-none">Real results from real builders.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-3">
            {[
              { q: "We swapped three tools for Vendora and our payout reconciliation went from 8 hours to 8 minutes.", a: "Priya S., Northern Drift" },
              { q: "The AI marketing module wrote my entire spring catalog in 45 minutes. Conversions are up 27%.", a: "Diego M., Diego's Picks" },
              { q: "Centralized payments mean I don't have to argue with Square or PayPal at 11pm anymore.", a: "Marc T., North Forge" },
            ].map((t) => (
              <div key={t.a} className="v-card flex flex-col">
                <div className="text-accent-blue flex">{[1,2,3,4,5].map(i => <Star key={i} size={14} fill="currentColor" />)}</div>
                <p className="mt-4 text-foreground leading-relaxed">"{t.q}"</p>
                <div className="mt-6 pt-4 border-t border-border text-sm text-muted-foreground">{t.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="v-section-pad">
        <div className="max-w-5xl mx-auto px-6 v-card border-primary border-2 text-center p-12 lg:p-20">
          <div className="label-eyebrow text-secondary mb-4">Ready when you are</div>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-primary leading-none">
            Source. Sell. Ship. Grow.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-xl mx-auto">Join the platform built for Canadian operators who want to ship faster, sell smarter, and grow without the duct tape.</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" className="v-btn-primary px-8 py-3 text-base" data-testid="final-cta-start">Create my account <ArrowRight size={16} /></Link>
            <Link to="/contact" className="v-btn-outline px-8 py-3 text-base" data-testid="final-cta-contact">Talk to sales</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Row({ left, right }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-2">
      <span className="text-white/80">{left}</span>
      <span className="text-white">{right}</span>
    </div>
  );
}
